import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN TOKENS - Golden Ratio & Color Science
// ═══════════════════════════════════════════════════════════════════════════════
const PHI = 1.618; // Golden ratio
const ease = [0.25, 0.1, 0.25, 1]; // Apple-like easing

// Color palette - Single-hue blue system with controlled tonal variation
const COLORS = {
  center: "#0B1D33",      // Deepest navy - gravitational anchor
  gold: "#8B7355",        // Warm accent - precious, intentional
  impact: "#3D6E96",      // Cool steel - sturdy, institutional
  innovation: "#2B7D75",  // Cool teal - fresh, forward-looking
  investment: "#1E3650",  // Deep navy - serious, foundational
  insight: "#22606F",     // Marine - depth, knowledge
};

// Engine data with associations
// With 45° base rotation: position 0=Right, 1=Bottom, 2=Left, 3=Top
const ENGINES = [
  { 
    id: "innovation", 
    title: "INNOVATION", 
    color: COLORS.innovation,
    hoverColor: "#359A90",
    position: 0, // Right
    marquee: "Innovations attuned for impact within real-world health systems, in real time",
    detail: "Introduce, integrate and scale at least 20 scale-ready/close-to-scale-ready innovations into routine care across national health systems",
    partners: ["private", "academia"]
  },
  { 
    id: "investment", 
    title: "INVESTMENT", 
    color: COLORS.investment,
    hoverColor: "#2A4A6A",
    position: 1, // Bottom
    marquee: "Pooled financing, built on the nucleus of public financing",
    detail: "Mobilize and align public, philanthropic, and catalytic blended financing behind country-defined goals",
    partners: ["philanthropy", "multilateral"]
  },
  { 
    id: "insight", 
    title: "INSIGHT &\nINFLUENCE", 
    color: COLORS.insight,
    hoverColor: "#2D7585",
    position: 2, // Left
    marquee: "Insights to inspire and influence more action, regionally and globally",
    detail: "A global plug-and-play insight-to-influence platform curating insights/evidence, generating learning from country programs, and channeling it to the people who set policy and allocate resources",
    partners: ["academia", "implementers", "clinical"]
  },
  { 
    id: "impact", 
    title: "IMPACT", 
    color: COLORS.impact,
    hoverColor: "#4A7FA8",
    position: 3, // Top
    marquee: "National ambition backed by system readiness and technical leadership",
    detail: "Support national governments (starting in Kenya and India) in setting and delivering audacious goals through system readiness, introduction and scaling of innovations, and provision of technical assistance across their highest-burden geographies",
    partners: ["governments", "multilateral"]
  },
];

// Partner data with icons and positions
const PARTNERS = [
  { id: "governments", name: "Governments & MoH", angle: 0 },
  { id: "clinical", name: "Clinical Associations", angle: 51.4 },
  { id: "multilateral", name: "Multilateral Partners", angle: 102.8 },
  { id: "private", name: "Private Sector", angle: 154.3 },
  { id: "philanthropy", name: "Philanthropic Finance", angle: 205.7 },
  { id: "academia", name: "Academic Institutions", angle: 257.1 },
  { id: "implementers", name: "Implementing Partners", angle: 308.6 },
];

// ═══════════════════════════════════════════════════════════════════════════════
// FADE IN COMPONENT - Apple-level timing
// ═══════════════════════════════════════════════════════════════════════════════
function FadeIn({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// GOLDEN RATIO INFOGRAPHIC - Premium, Jony Ive Level
// ═══════════════════════════════════════════════════════════════════════════════
function GoldenInfographic({ onEngineSelect, selectedEngine }) {
  const [hoveredEngine, setHoveredEngine] = useState(null);
  const [isDrawn, setIsDrawn] = useState(false);
  const [showShimmer, setShowShimmer] = useState(false);
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  // Entrance animation sequence
  useEffect(() => {
    if (isInView) {
      const drawTimer = setTimeout(() => setIsDrawn(true), 100);
      const shimmerTimer = setTimeout(() => setShowShimmer(true), 1100);
      return () => {
        clearTimeout(drawTimer);
        clearTimeout(shimmerTimer);
      };
    }
  }, [isInView]);

  // Golden ratio geometry calculations
  const size = 560;
  const cx = 320; // Center horizontally in wider viewBox
  const cy = size / 2;
  const R = 78; // Base radius unit
  
  // Golden ratio derived radii - precise measurements
  const centerRadius = R;
  const goldRingRadius = R * 1.05;
  const guardRingRadius = R * 1.09;
  const engineInnerRadius = R * 1.18;
  const engineOuterRadius = R * PHI * 1.52; // ~190px
  const ghostRingRadius = engineOuterRadius + 16; // Ghost ring just outside engines
  const PARTNER_ORBIT_RADIUS = ghostRingRadius + 50; // SINGLE orbit for ALL partners - ~256px

  const activeEngine = hoveredEngine;

  // Arc path generator for quadrants (45° rotated diamond orientation)
  const arcPath = (quadrantIndex, innerR, outerR) => {
    const baseAngle = 45;
    const startAngle = (quadrantIndex * 90 + baseAngle - 90) * Math.PI / 180;
    const endAngle = ((quadrantIndex + 1) * 90 + baseAngle - 90) * Math.PI / 180;
    
    const x1 = cx + innerR * Math.cos(startAngle);
    const y1 = cy + innerR * Math.sin(startAngle);
    const x2 = cx + outerR * Math.cos(startAngle);
    const y2 = cy + outerR * Math.sin(startAngle);
    const x3 = cx + outerR * Math.cos(endAngle);
    const y3 = cy + outerR * Math.sin(endAngle);
    const x4 = cx + innerR * Math.cos(endAngle);
    const y4 = cy + innerR * Math.sin(endAngle);
    
    return `M ${x1} ${y1} L ${x2} ${y2} A ${outerR} ${outerR} 0 0 1 ${x3} ${y3} L ${x4} ${y4} A ${innerR} ${innerR} 0 0 0 ${x1} ${y1}`;
  };

  // Get label position for each quadrant
  const getLabelPosition = (quadrantIndex) => {
    const baseAngle = 45;
    const midAngle = (quadrantIndex * 90 + 45 + baseAngle - 90) * Math.PI / 180;
    const labelRadius = (engineInnerRadius + engineOuterRadius) / 2;
    return {
      x: cx + labelRadius * Math.cos(midAngle),
      y: cy + labelRadius * Math.sin(midAngle)
    };
  };

  // Refined, industry-standard partner icons (minimal, geometric, professional)
  const partnerIcons = {
    governments: (
      <g strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 2L13 6H1L7 2Z" fill="none" />
        <path d="M2 6V12H5V9H9V12H12V6" fill="none" />
        <path d="M1 12H13" />
      </g>
    ),
    clinical: (
      <g strokeWidth="1.5" strokeLinecap="round">
        <path d="M7 3V11" />
        <path d="M3 7H11" />
      </g>
    ),
    multilateral: (
      <g strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <circle cx="7" cy="7" r="5.5" />
        <path d="M1.5 7H12.5" />
        <path d="M7 1.5C9 3.5 9 10.5 7 12.5" />
        <path d="M7 1.5C5 3.5 5 10.5 7 12.5" />
      </g>
    ),
    private: (
      <g strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d="M7 2L12 12H2L7 2Z" />
      </g>
    ),
    philanthropy: (
      <g strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d="M7 12C7 12 2 8.5 2 5.5C2 3.5 3.5 2 5.5 2C6.5 2 7 2.5 7 3C7 2.5 7.5 2 8.5 2C10.5 2 12 3.5 12 5.5C12 8.5 7 12 7 12Z" />
      </g>
    ),
    academia: (
      <g strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d="M7 1L13 4L7 7L1 4L7 1Z" />
        <path d="M3 5.5V9.5L7 11.5L11 9.5V5.5" />
      </g>
    ),
    implementers: (
      <g strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <circle cx="7" cy="3" r="2" />
        <path d="M7 6V9" />
        <circle cx="3" cy="11" r="1.5" />
        <circle cx="11" cy="11" r="1.5" />
        <path d="M4.5 10L7 9" />
        <path d="M9.5 10L7 9" />
      </g>
    )
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-[640px] mx-auto px-4">
      <svg 
        viewBox="0 0 640 560"
        className="w-full h-auto overflow-visible"
        role="img"
        aria-label="THBI Framework: Four engines of change - Impact, Innovation, Investment, and Insight & Influence"
        style={{ overflow: 'visible' }}
      >
        <defs>
          <radialGradient id="centerGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#0F2540" />
            <stop offset="100%" stopColor={COLORS.center} />
          </radialGradient>
          {/* Subtle glow for halo */}
          <filter id="haloGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Ghost ring / Halo - more visible */}
        <motion.circle
          cx={cx} cy={cy} r={ghostRingRadius}
          fill="none"
          stroke={COLORS.center}
          strokeWidth="1.5"
          strokeOpacity="0.15"
          initial={{ opacity: 0 }}
          animate={isDrawn ? { opacity: 1 } : {}}
          transition={{ duration: 0.35, delay: 0.55 }}
        />
        
        {/* Secondary subtle halo for depth */}
        <motion.circle
          cx={cx} cy={cy} r={ghostRingRadius}
          fill="none"
          stroke={COLORS.center}
          strokeWidth="8"
          strokeOpacity="0.04"
          initial={{ opacity: 0 }}
          animate={isDrawn ? { opacity: 1 } : {}}
          transition={{ duration: 0.35, delay: 0.55 }}
        />

        {/* Partner labels - ALL at EXACT SAME distance from dots */}
        {PARTNERS.map((partner, i) => {
          const angle = (partner.angle - 90) * Math.PI / 180;
          
          // Dot position - on the ghost ring
          const dotX = cx + ghostRingRadius * Math.cos(angle);
          const dotY = cy + ghostRingRadius * Math.sin(angle);
          
          // FIXED DISTANCE from dot to label: exactly 35px for ALL labels
          const LABEL_DISTANCE = 35;
          const labelX = cx + (ghostRingRadius + LABEL_DISTANCE) * Math.cos(angle);
          const labelY = cy + (ghostRingRadius + LABEL_DISTANCE) * Math.sin(angle);
          
          // Text anchor based on quadrant
          let textAnchor = "middle";
          if (partner.angle > 45 && partner.angle < 135) {
            textAnchor = "start"; // Right side
          } else if (partner.angle > 225 && partner.angle < 315) {
            textAnchor = "end"; // Left side
          }

          return (
            <motion.g 
              key={partner.id}
              initial={{ opacity: 0 }}
              animate={isDrawn ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.7 + i * 0.05 }}
            >
              {/* Dot on ghost ring */}
              <circle
                cx={dotX} cy={dotY} r="4"
                fill="#0B1D33"
                fillOpacity="0.15"
              />
              <circle
                cx={dotX} cy={dotY} r="2"
                fill="#0B1D33"
                fillOpacity="0.4"
              />
              
              {/* Partner label - EXACT same distance from dot for ALL */}
              <text
                x={labelX}
                y={labelY}
                textAnchor={textAnchor}
                dominantBaseline="middle"
                className="font-sans"
                style={{ 
                  fontSize: '11px', 
                  fontWeight: 500,
                  fill: '#0B1D33',
                  fillOpacity: 0.5
                }}
              >
                {partner.name}
              </text>
            </motion.g>
          );
        })}

        {/* Engine quadrants */}
        {ENGINES.map((engine, i) => {
          const isActive = activeEngine === engine.id;
          const isDimmed = activeEngine && !isActive;
          const labelPos = getLabelPosition(i);
          
          return (
            <motion.g
              key={engine.id}
              onMouseEnter={() => {
                setHoveredEngine(engine.id);
                onEngineSelect(engine.id);
              }}
              onMouseLeave={() => {
                setHoveredEngine(null);
                onEngineSelect(null);
              }}
              style={{ cursor: "pointer", outline: "none" }}
              role="button"
              aria-label={`${engine.title}: ${engine.marquee}`}
              tabIndex={0}
              className="focus:outline-none"
            >
              {/* Quadrant fill */}
              <motion.path
                d={arcPath(i, engineInnerRadius, engineOuterRadius)}
                fill={isActive ? engine.hoverColor : engine.color}
                initial={{ opacity: 0, scale: 0.86 }}
                animate={isDrawn ? { 
                  opacity: isDimmed ? 0.2 : 0.92,
                  scale: isActive ? 1.02 : 1
                } : { opacity: 0, scale: 0.86 }}
                transition={{ 
                  duration: 0.5, 
                  delay: 0.1 + i * 0.1,
                  ease: [0.25, 0.1, 0.25, 1]
                }}
                style={{ transformOrigin: `${cx}px ${cy}px` }}
              />
              
              {/* Quadrant divider line */}
              {i > 0 && (
                <motion.line
                  x1={cx + engineInnerRadius * Math.cos((i * 90 + 45 - 90) * Math.PI / 180)}
                  y1={cy + engineInnerRadius * Math.sin((i * 90 + 45 - 90) * Math.PI / 180)}
                  x2={cx + engineOuterRadius * Math.cos((i * 90 + 45 - 90) * Math.PI / 180)}
                  y2={cy + engineOuterRadius * Math.sin((i * 90 + 45 - 90) * Math.PI / 180)}
                  stroke="white"
                  strokeWidth="3"
                  strokeOpacity={0.15}
                  initial={{ pathLength: 0 }}
                  animate={isDrawn ? { pathLength: 1 } : {}}
                  transition={{ duration: 0.3, delay: 0.4 + i * 0.05 }}
                />
              )}
              
              {/* Engine label */}
              <motion.g
                initial={{ opacity: 0 }}
                animate={isDrawn ? { opacity: isDimmed ? 0.3 : 1 } : {}}
                transition={{ duration: 0.3, delay: 0.5 + i * 0.1 }}
              >
                {engine.title.split('\n').map((line, lineIndex) => (
                  <text
                    key={lineIndex}
                    x={labelPos.x}
                    y={labelPos.y + (lineIndex - (engine.title.split('\n').length - 1) / 2) * 14}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="font-sans pointer-events-none select-none"
                    style={{ 
                      fontSize: '12px', 
                      fontWeight: 500,
                      letterSpacing: '2px',
                      fill: 'white',
                      fillOpacity: 0.85
                    }}
                  >
                    {line}
                  </text>
                ))}
              </motion.g>
            </motion.g>
          );
        })}

        {/* Center circle - mission anchor */}
        <motion.circle
          cx={cx} cy={cy} r={centerRadius}
          fill="url(#centerGradient)"
          initial={{ scale: 0.86, opacity: 0 }}
          animate={isDrawn ? { scale: 1, opacity: 1 } : {}}
          transition={{ 
            duration: 0.5,
            ease: [0.25, 0.1, 0.25, 1]
          }}
          style={{ transformOrigin: `${cx}px ${cy}px` }}
        />
        
        {/* Gold accent ring */}
        <motion.circle
          cx={cx} cy={cy} r={goldRingRadius}
          fill="none"
          stroke={COLORS.gold}
          strokeWidth="1.2"
          initial={{ opacity: 0 }}
          animate={isDrawn ? { 
            opacity: showShimmer ? [0.28, 0.45, 0.28] : 0.28
          } : {}}
          transition={showShimmer ? { 
            duration: 3, 
            times: [0, 0.5, 1],
            ease: [0.4, 0, 0.6, 1]
          } : { duration: 0.3, delay: 0.3 }}
        />
        
        {/* White guard ring */}
        <motion.circle
          cx={cx} cy={cy} r={guardRingRadius}
          fill="none"
          stroke="white"
          strokeWidth="0.3"
          strokeOpacity="0.3"
          initial={{ opacity: 0 }}
          animate={isDrawn ? { opacity: 1 } : {}}
          transition={{ duration: 0.3, delay: 0.35 }}
        />

        {/* Center mission text - EXACTLY centered using SVG text */}
        <motion.g
          initial={{ opacity: 0 }}
          animate={isDrawn ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <text
            x={cx}
            y={cy - 24}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="white"
            fillOpacity="0.82"
            fontSize="9"
            fontWeight="500"
            fontFamily="system-ui, sans-serif"
          >
            Accelerated reduction
          </text>
          <text
            x={cx}
            y={cy - 12}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="white"
            fillOpacity="0.82"
            fontSize="9"
            fontWeight="500"
            fontFamily="system-ui, sans-serif"
          >
            of low birth weight and
          </text>
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="white"
            fillOpacity="0.82"
            fontSize="9"
            fontWeight="500"
            fontFamily="system-ui, sans-serif"
          >
            improved outcomes for
          </text>
          <text
            x={cx}
            y={cy + 12}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="white"
            fillOpacity="0.82"
            fontSize="9"
            fontWeight="500"
            fontFamily="system-ui, sans-serif"
          >
            small and sick newborns
          </text>
        </motion.g>
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DETAIL CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
function DetailCard({ engine, onClose }) {
  if (!engine) return null;
  
  const engineData = ENGINES.find(e => e.id === engine);
  if (!engineData) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
      className="relative bg-white rounded-2xl border border-[#e0e0e0] shadow-[0_4px_24px_rgba(0,0,0,0.06)] overflow-hidden"
    >
      {/* Color accent bar */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl"
        style={{ backgroundColor: engineData.color }}
      />
      
      <div className="pl-7 pr-6 py-6">
        {/* Engine name */}
        <p 
          className="font-sans text-[11px] font-medium uppercase tracking-[2px] mb-3"
          style={{ color: engineData.color }}
        >
          {engineData.title.replace('\n', ' ')}
        </p>
        
        {/* Marquee statement */}
        <h3 
          className="font-sans text-[15px] font-medium leading-[1.55] mb-3"
          style={{ color: COLORS.center }}
        >
          {engineData.marquee}
        </h3>
        
        {/* Detail text */}
        <p 
          className="font-sans text-[13px] font-normal leading-[1.7] mb-5"
          style={{ color: COLORS.center, opacity: 0.42 }}
        >
          {engineData.detail}
        </p>
        
        {/* CTA Button */}
        <button
          onClick={() => {
            const section = document.getElementById(engine);
            if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[20px] border text-[11px] font-medium transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
          style={{ 
            borderColor: COLORS.gold, 
            color: COLORS.gold 
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = COLORS.gold;
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = COLORS.gold;
          }}
        >
          Explore this engine
          <ArrowRight size={12} />
        </button>
      </div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DATA
// ═══════════════════════════════════════════════════════════════════════════════
const impactAreas = [
  { title: "System readiness", items: ["Workforce competency and clinical skill building", "Diagnostics, commodities, and supply chain strengthening", "New medicine formulations, including MMS and parenteral iron", "Neonatal unit equipment and biomedical engineering", "Digital health infrastructure for frontline care", "Demand generation for early antenatal care", "Monitoring, accountability, and quality improvement"] },
  { title: "Innovation introduction and scaling", items: ["Product innovations across diagnostics, devices, and therapeutics", "AI-enabled clinical tools such as point-of-care ultrasound", "Risk stratification and predictive decision-support tools", "Service delivery innovations that improve birth outcomes at scale"] },
  { title: "Insight and influence", items: ["Implementation learning and evidence generation through country learning labs", "Contributions to the global birth outcomes knowledge platform", "Strategic communications, advocacy, and policy influence"] },
  { title: "Technical assistance", items: ["System readiness assessment and strengthening", "Innovation adoption, integration, and scale-up support", "Data systems, analytics, and performance monitoring", "Partnership development and resource mobilization"] },
];

const innovationTypes = [
  { title: "Products and technologies", desc: "New and improved diagnostics, devices, digital tools, and decision-support systems that bring clinical precision closer to the point of care." },
  { title: "Clinical interventions", desc: "Evidence-backed maternal and newborn interventions with the strongest proof of impact and the greatest potential for reach across health systems." },
  { title: "Service delivery innovations", desc: "New models of organizing, delivering, and improving care that make health systems work better for mothers and newborns at every level." },
];

const innovationStages = [
  { step: "A", title: "Source & Prioritize", desc: "Co-define priority birth outcome challenges and innovation areas with MoH and Professional Associations. Actively scope innovations and run targeted calls with partners." },
  { step: "B", title: "Prepare for Scale", desc: "Develop ready-to-introduce innovation packages for government adoption. Conduct public-sector scaling readiness assessment." },
  { step: "C", title: "Contextualize & Evidence", desc: "Work with MoH to introduce innovations in select facilities as Learning Labs. Generate locally-relevant evidence." },
  { step: "D", title: "Enable System Readiness", desc: "Convene stakeholders to review and update clinical protocols. Develop training packages and monitoring systems." },
  { step: "E", title: "Mobilize for Scale", desc: "Host yearly Investor Circle to connect innovations with donors, investors, and governments for procurement and scale-up." },
];

const financingBuckets = [
  { title: "Public Financing", desc: "Government funding for health workforce, facility infrastructure, essential equipment and medicines" },
  { title: "International Philanthropy", desc: "Innovation introduction and scale-up, technical assistance, Learning Labs" },
  { title: "Local Philanthropy & CSR", desc: "Digital health infrastructure, neonatal unit equipment, commodity donations" },
  { title: "Blended Financing", desc: "Loan guarantees, outcome-based financing, results-linked incentives" },
];

const insightOfferings = [
  { title: "Knowledge Hub", desc: "Open access microsite highlighting key publications, toolkits, and learning materials from members." },
  { title: "AI Discovery Engine", desc: "AI enabled information discovery engine allowing rapidly discover evidence, guidance, and implementation resources." },
  { title: "Learning Labs Network", desc: "Healthy Birth Initiative Learning Labs: Country-based platforms testing solutions, generating implementation evidence, and sharing lessons." },
  { title: "Innovation Marketplace", desc: "Innovation marketplace highlighting scalable tools, technologies, and program models including scale readiness profiles." },
  { title: "Policy Roundtables", desc: "Policy roundtables and global convenings convened alongside global forums such as WHA, UNGA, and the World Health Summit." },
  { title: "Expert Network", desc: "Curated Pool of Technical Experts: Pool of vetted technical experts providing support to countries and partners." },
];

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function FrameworkPage() {
  const [selectedEngine, setSelectedEngine] = useState(null);
  const impactRef = useRef(null);
  const innovationRef = useRef(null);
  const investmentRef = useRef(null);
  const insightRef = useRef(null);
  const refs = { impact: impactRef, innovation: innovationRef, investment: investmentRef, insight: insightRef };

  const handleEngineSelect = useCallback((id) => {
    setSelectedEngine(prev => prev === id ? null : id);
  }, []);

  const scrollToSection = (id) => {
    setTimeout(() => refs[id]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  return (
    <div data-testid="framework-page" className="pt-16 bg-white">
      
      {/* Header - Generous spacing */}
      <section className="py-40 md:py-52">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FadeIn>
            <p className="text-[#E8A33D] text-[10px] tracking-[0.4em] uppercase mb-10 font-medium">
              Framework for Action
            </p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl xl:text-[56px] text-[#0B1D33] font-semibold tracking-[-0.02em] leading-[1.15]">
              Four Engines Working for One Mission
            </h1>
          </FadeIn>
        </div>
      </section>

      {/* Infographic Section */}
      <section className="pb-32 md:pb-40">
        <div className="max-w-4xl mx-auto px-6">
          <FadeIn className="text-center mb-10">
            <p className="text-[#0B1D33]/25 text-[11px] font-medium tracking-wide">
              Hover to explore each engine.
            </p>
          </FadeIn>
          
          <GoldenInfographic 
            onEngineSelect={handleEngineSelect}
            selectedEngine={selectedEngine}
          />
          
          {/* Detail Card */}
          <div className="mt-8 max-w-lg mx-auto min-h-[200px]">
            <AnimatePresence mode="wait">
              {selectedEngine && (
                <DetailCard 
                  key={selectedEngine}
                  engine={selectedEngine} 
                  onClose={() => setSelectedEngine(null)}
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Engine 1: Impact */}
      <section ref={impactRef} id="impact" className="bg-[#F8F9FA] py-40 md:py-52 scroll-mt-20">
        <div className="max-w-5xl mx-auto px-6">
          <FadeIn>
            <p className="text-[#E8A33D] text-xs tracking-[0.3em] uppercase mb-4">Engine 01</p>
            <h2 className="font-heading text-4xl md:text-5xl text-[#0B1D33] font-semibold tracking-tight mb-6">Impact</h2>
            <p className="text-[#0B1D33]/50 text-xl italic mb-4 max-w-2xl">"National ambition backed by system readiness and technical leadership."</p>
            <p className="text-slate-500 text-base mb-16 max-w-3xl leading-relaxed">The initiative will work with national governments (starting in Kenya and India) to define and deliver audacious goals through system readiness, introduction and scaling of innovations, and provision of technical assistance across their highest-burden geographies.</p>
          </FadeIn>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <FadeIn delay={0.1}>
              <div className="bg-[#0B1D33] rounded-3xl p-10">
                <p className="text-white/30 text-xs tracking-wider uppercase mb-2">Kenya Goal</p>
                <p className="text-white text-5xl font-heading font-light mb-2">50%</p>
                <p className="text-white/50 text-sm">reduction in neonatal deaths by 2030</p>
              </div>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div className="bg-[#E8A33D] rounded-3xl p-10">
                <p className="text-white/70 text-xs tracking-wider uppercase mb-2">Kenya Goal</p>
                <p className="text-white text-5xl font-heading font-light mb-2">30%</p>
                <p className="text-white/80 text-sm">reduction in low birthweight by 2030</p>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.3}>
            <h3 className="text-xl font-semibold text-[#0B1D33] mb-8">What We Will Focus On</h3>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {impactAreas.map((area, i) => (
              <FadeIn key={area.title} delay={0.3 + i * 0.1}>
                <h4 className="text-[#0B1D33] font-semibold mb-4">{area.title}</h4>
                <ul className="space-y-2">
                  {area.items.map((item, j) => (
                    <li key={j} className="text-slate-500 text-sm flex items-start gap-3">
                      <span className="text-[#E8A33D] mt-1">·</span>{item}
                    </li>
                  ))}
                </ul>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Engine 2: Innovation */}
      <section ref={innovationRef} id="innovation" className="bg-white py-32 md:py-40 scroll-mt-20">
        <div className="max-w-5xl mx-auto px-6">
          <FadeIn>
            <p className="text-[#E8A33D] text-xs tracking-[0.3em] uppercase mb-4">Engine 02</p>
            <h2 className="font-heading text-4xl md:text-5xl text-[#0B1D33] font-semibold tracking-tight mb-6">Innovation</h2>
            <p className="text-[#0B1D33]/50 text-xl italic mb-4 max-w-2xl">"Innovations attuned for impact within real-world health systems, in real time."</p>
            <p className="text-slate-500 text-base mb-16 max-w-3xl leading-relaxed">Over the next five years, we will work with governments and partners to scale <strong className="text-[#0B1D33]">20 high-impact innovations</strong> that improve birth outcomes and strengthen care for small and sick newborns, with a focus on three types of innovations:</p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {innovationTypes.map((type, i) => (
              <FadeIn key={type.title} delay={i * 0.1}>
                <div className="bg-[#0B1D33] rounded-3xl p-8">
                  <h4 className="text-white font-semibold mb-3">{type.title}</h4>
                  <p className="text-white/40 text-sm leading-relaxed">{type.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn>
            <h3 className="text-xl font-semibold text-[#0B1D33] mb-10">A 5-Staged Journey from Introduction to Scale</h3>
          </FadeIn>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {innovationStages.map((stage, i) => (
              <FadeIn key={stage.step} delay={0.1 + i * 0.1}>
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#0B1D33] text-white rounded-full flex items-center justify-center mx-auto mb-4 font-semibold">{stage.step}</div>
                  <h4 className="text-[#0B1D33] font-semibold text-sm mb-2">{stage.title}</h4>
                  <p className="text-slate-400 text-xs leading-relaxed">{stage.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Engine 3: Investment */}
      <section ref={investmentRef} id="investment" className="bg-[#F8F9FA] py-32 md:py-40 scroll-mt-20">
        <div className="max-w-5xl mx-auto px-6">
          <FadeIn>
            <p className="text-[#E8A33D] text-xs tracking-[0.3em] uppercase mb-4">Engine 03</p>
            <h2 className="font-heading text-4xl md:text-5xl text-[#0B1D33] font-semibold tracking-tight mb-6">Investment</h2>
            <p className="text-[#0B1D33]/50 text-xl italic mb-4 max-w-2xl">"Pooled financing, built on the nucleus of public financing."</p>
            <p className="text-slate-500 text-base mb-6 max-w-3xl leading-relaxed">Mobilize and align public, philanthropic and catalytic blended financing behind country-defined goals.</p>
            <p className="text-slate-500 text-base mb-16 max-w-3xl leading-relaxed">This initiative will be anchored around prioritized public financing. These public commitments will be supplemented by Jhpiego's catalytic investment to unlock further resources for system readiness, innovation adoption, and technical assistance.</p>
          </FadeIn>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {financingBuckets.map((bucket, i) => (
              <FadeIn key={bucket.title} delay={i * 0.1}>
                <h4 className="text-[#0B1D33] font-semibold text-sm mb-2">{bucket.title}</h4>
                <p className="text-slate-400 text-xs leading-relaxed">{bucket.desc}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Engine 4: Insight */}
      <section ref={insightRef} id="insight" className="bg-white py-32 md:py-40 scroll-mt-20">
        <div className="max-w-5xl mx-auto px-6">
          <FadeIn>
            <p className="text-[#E8A33D] text-xs tracking-[0.3em] uppercase mb-4">Engine 04</p>
            <h2 className="font-heading text-4xl md:text-5xl text-[#0B1D33] font-semibold tracking-tight mb-6">Insight & Influence</h2>
            <p className="text-[#0B1D33]/50 text-xl italic mb-4 max-w-2xl">"Insights to inspire and influence more action, regionally and globally."</p>
            <p className="text-slate-500 text-base mb-16 max-w-3xl leading-relaxed">A global plug-and-play insight to influence platform curating insights/evidence, generating learning from country programs, and channeling it to the people who set policy and allocate resources.</p>
          </FadeIn>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {insightOfferings.map((item, i) => (
              <FadeIn key={item.title} delay={i * 0.08}>
                <h4 className="text-[#0B1D33] font-semibold text-sm mb-2">{item.title}</h4>
                <p className="text-slate-400 text-xs leading-relaxed">{item.desc}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0B1D33] py-32 md:py-40">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <FadeIn>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white font-semibold tracking-tight mb-6">Ready to be part of this?</h2>
            <p className="text-white/40 text-base mb-12">Whether you are a government seeking solutions, an innovator looking for pathways to public-sector scale, or an investor seeking high-impact opportunities.</p>
            <Link to="/contact">
              <motion.button 
                className="inline-flex items-center gap-3 bg-white text-[#0B1D33] px-10 py-5 rounded-full font-medium text-sm"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Start a Conversation <ArrowRight size={16} />
              </motion.button>
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
